import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { useMsal } from '@azure/msal-react';
import NavbarMenuTsoPq from '@components/Menu/NavbarMenuTsoPq';
import { userMenuOptions } from '@constants/userMenu';
import { UserMenuProps } from '@custom-types/NavbarTypes';
import { AccountCircle } from '@mui/icons-material';
import { Avatar, Box, Divider, MenuItem, Popper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const StyledBox = styled(Box)(() => {
  return {
    width: '15.125rem',
    height: '2.625rem',
    display: 'flex',
    justifyContent: 'flex-end',
    color: tsoPqTheme.palette.customTextColor.color_font_ft_helping_text,
    '& .MuiButtonBase-root': {
      padding: '0 0.5rem',
      '&:hover': {
        background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_accent,
      },
      '&:active': {
        background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_pressed_lt,
      },
    },
  };
});

const StyledMenuItem = styled(MenuItem)(() => {
  return { minWidth: '8.75rem', minHeight: '1.875rem' };
});

const StyledMenuItemContainer = styled(Box)(() => {
  return { '&:hover': { background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim } };
});

const StyledLabel = styled(Typography)(() => {
  return { padding: '0.25rem 0.5rem' };
});

const StyledAccountCircle = styled(AccountCircle)(() => {
  return { color: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_accent, fontSize: '1.5rem' };
});

const StyledUserProfile = styled(Avatar)(() => {
  return {
    height: '1.5rem',
    width: '1.5rem',
    fontSize: '0.75rem',
    background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_accent,
    margin: 'auto auto auto 0.5rem',
  };
});

const StyledEnvLabel = styled(Typography)(() => {
  return {
    background: 'red',
    width: '15rem',
    textAlign: 'center',
    fontSize: '0.75rem',
    color: 'white',
    fontWeight: '200',
    margin: 'auto',
    padding: '4px',
    borderRadius: '4px',
  };
});

const UserMenuTsoPq = () => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  const { instance } = useMsal();
  const { t } = useTranslation();

  const account = instance.getActiveAccount();

  const userMenu = userMenuOptions;
  const popperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    userMenu.forEach((menuItem) => {
      if (menuItem.label === 'ROLE') {
        menuItem.label = `${account?.idTokenClaims?.roles?.join(', ')}`;
      }
    });
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (popperRef.current && !popperRef.current.contains(event.target as Node)) {
      handleClose();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElNav(null);
  };

  const getInitials = (name: string): string => {
    const names = name.trim().split(' ');
    const initials = names.map((name) => name[0]);
    return initials.join('');
  };

  const changeUser = () => {
    instance.acquireTokenRedirect({
      prompt: 'select_account',
      scopes: [`api://${import.meta.env.VITE_UENBPQ_API_CLIENT_ID}/access_as_user`],
    });
  };

const logOut = () =>
  instance.logoutRedirect({
    account: instance.getActiveAccount() ?? undefined,
  });

  const actionsMap = { changeUser, logOut };
  const isProduction = import.meta.env.VITE_UENBPQ_ENVIRONMENT === 'prod';

  const { data: tsos } = useGetTsoGetAllQuery();

  return (
    <StyledBox data-testid="userMenu">
      {isProduction ? (
        <StyledEnvLabel data-testid="navbarEnvLabel">Nicht produktiver MVP-Stand</StyledEnvLabel>
      ) : (
        <StyledEnvLabel data-testid="navbarEnvLabel">
          {import.meta.env.VITE_UENBPQ_ENVIRONMENT}/
          {tsos?.externalDtos?.find((t) => t.isLoggedInUsersTso)?.abbreviation}
        </StyledEnvLabel>
      )}
      <MenuItem onClick={(event) => handleClick(event)} disableRipple>
        <>
          {account?.name ? (
            <>
              <Typography fontSize={'0.75rem'}>{account.name.replace(',', '')}</Typography>
              <StyledUserProfile>{getInitials(account.name)}</StyledUserProfile>
            </>
          ) : (
            <StyledAccountCircle />
          )}
        </>
      </MenuItem>
      <Popper
        ref={popperRef}
        data-testid="userMenuOptions"
        anchorEl={anchorElNav}
        keepMounted
        disablePortal
        open={Boolean(anchorElNav)}
      >
        <NavbarMenuTsoPq>
          {userMenu?.map((menuItem: UserMenuProps) => (
            <StyledMenuItemContainer key={menuItem.id}>
              <StyledMenuItem disableRipple onClick={actionsMap[menuItem.action as keyof typeof actionsMap]}>
                <StyledLabel>{menuItem.id === 1 ? menuItem.label : t(menuItem.label)}</StyledLabel>
              </StyledMenuItem>
              {menuItem.separator && <Divider />}
            </StyledMenuItemContainer>
          ))}
        </NavbarMenuTsoPq>
      </Popper>
    </StyledBox>
  );
};

export default UserMenuTsoPq;
